import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
// StrictMode ile uyum için F (fonksiyonel) bileşenler gerekli; class sürümleri çift mount'ta haritada yetim kopya bırakıyor.
import { CircleF as Circle, GoogleMap, MarkerF as Marker, OverlayViewF, FLOAT_PANE, useLoadScript } from "@react-google-maps/api";
import html2canvas from "html2canvas";
import { getSesMetrics } from "../../utils/mobility";
import { normalizeUnitType } from "../../utils/unitTypes";

const UNIT_TYPE_COLORS = {
  CLP: "#2563eb",
  Billboard: "#f97316",
  Giantboard: "#7c3aed",
  Megalight: "#0891b2",
  Megaboard: "#dc2626",
  Duvar: "#16a34a",
};

const DEFAULT_MARKER_COLOR = "#64748b";
const AUTO_UNIT_COLORS = [
  "#0f766e",
  "#be123c",
  "#9333ea",
  "#ca8a04",
  "#0284c7",
  "#c2410c",
  "#4f46e5",
  "#15803d",
  "#a21caf",
  "#0e7490",
  "#b45309",
  "#475569",
];
const autoColorForUnitType = (unitType) => {
  const normalized = normalizeUnitType(unitType);
  if (!normalized) return DEFAULT_MARKER_COLOR;
  let hash = 0;
  for (let index = 0; index < normalized.length; index += 1) {
    hash = ((hash << 5) - hash) + normalized.charCodeAt(index);
    hash |= 0;
  }
  return AUTO_UNIT_COLORS[Math.abs(hash) % AUTO_UNIT_COLORS.length];
};
const markerColor = (unitType) => UNIT_TYPE_COLORS[normalizeUnitType(unitType)] || autoColorForUnitType(unitType);
const formatInteger = (value) => Math.round(Number(value) || 0).toLocaleString("tr-TR", { maximumFractionDigits: 0 });
const CLUSTER_COLOR = "#1e293b";

const clusterCellSizeForZoom = (zoomLevel) => {
  if (zoomLevel >= 16) return 0;
  if (zoomLevel >= 14) return 0.0025;
  if (zoomLevel >= 12) return 0.0075;
  if (zoomLevel >= 10) return 0.02;
  return 0.06;
};

const MARKER_OPTIONS = { optimized: true };

// Küme sayısını Google'ın ayrı label DOM'u yerine ikonun içine gömüyoruz:
// html2canvas ile PNG'ye aktarırken label'ın kendi transform katmanı sayının
// daireden kaymış görünmesine yol açıyordu; tek parça SVG ikonda bu risk yok.
const CLUSTER_ICON_CACHE = new Map();
const clusterIconForCount = (count) => {
  if (!window?.google?.maps) return undefined;
  if (CLUSTER_ICON_CACHE.has(count)) return CLUSTER_ICON_CACHE.get(count);
  const radius = 16;
  const size = radius * 2 + 4;
  const text = String(count);
  const fontSize = text.length > 2 ? 10 : 11.5;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
    `<circle cx="${size / 2}" cy="${size / 2}" r="${radius}" fill="${CLUSTER_COLOR}" fill-opacity="0.92" stroke="#ffffff" stroke-width="2" />` +
    `<text x="50%" y="51%" text-anchor="middle" dominant-baseline="central" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="700" fill="#ffffff">${text}</text>` +
    `</svg>`;
  const icon = {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(size / 2, size / 2),
  };
  CLUSTER_ICON_CACHE.set(count, icon);
  return icon;
};

// Heatmap yarıçapları metre cinsinden; zoom 12'nin altında ekranda görünür kalmaları için ölçeklenir.
const heatScaleForZoom = (zoomLevel) => Math.pow(2, Math.max(0, 12 - Math.round(zoomLevel)));

const HeatmapLayer = React.memo(function HeatmapLayer({ circles, scale }) {
  return (
    <>
      {circles.map((circle) => (
        <Circle
          key={circle.id}
          center={{ lat: circle.lat, lng: circle.lng }}
          radius={circle.radius * scale}
          options={{
            clickable: false,
            fillColor: circle.color,
            fillOpacity: circle.fillOpacity,
            strokeOpacity: 0,
            zIndex: 1,
          }}
        />
      ))}
    </>
  );
});

const MarkersLayer = React.memo(function MarkersLayer({ markers, markerIcons, onSelectPoint, onOpenCluster }) {
  return (
    <>
      {markers.map((marker) => {
        if (marker.kind === "cluster") {
          return (
            <Marker
              key={marker.id}
              position={{ lat: marker.lat, lng: marker.lng }}
              icon={clusterIconForCount(marker.count)}
              options={MARKER_OPTIONS}
              onClick={() => onOpenCluster(marker)}
            />
          );
        }

        const p = marker.point;
        return (
          <Marker
            key={p.id}
            position={{ lat: p.lat, lng: p.lng }}
            icon={markerIcons.get(normalizeUnitType(p.unitType))}
            options={MARKER_OPTIONS}
            onClick={() => onSelectPoint(p)}
          />
        );
      })}
    </>
  );
});

export default function MobilityMap({
  points = [],
  initialCenter,
  centerFallback = { lat: 41.04, lng: 28.99 },
  zoom = 12,
  title = "Lokasyon Haritası",
  subtitle = "",
  height = "65vh",
}) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const [active, setActive] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [hiddenUnitTypes, setHiddenUnitTypes] = useState(() => new Set());
  const [clusterCell, setClusterCell] = useState(() => clusterCellSizeForZoom(zoom));
  const [heatScale, setHeatScale] = useState(() => heatScaleForZoom(zoom));
  const mapExportRef = useRef(null);
  const mapRef = useRef(null);

  const validPoints = useMemo(
    () => points.filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng)),
    [points]
  );

  const center = useMemo(() => {
    if (initialCenter && Number.isFinite(initialCenter.lat) && Number.isFinite(initialCenter.lng)) return initialCenter;
    if (!validPoints.length) return centerFallback;
    const sum = validPoints.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
    return { lat: sum.lat / validPoints.length, lng: sum.lng / validPoints.length };
  }, [validPoints, centerFallback, initialCenter]);

  const legendItems = useMemo(() => {
    const counts = new Map();
    validPoints.forEach((point) => {
      const unitType = normalizeUnitType(point.unitType);
      if (!unitType) return;
      counts.set(unitType, (counts.get(unitType) || 0) + 1);
    });
    return Array.from(counts, ([unitType, count]) => ({
      unitType,
      count,
      color: markerColor(unitType),
    }));
  }, [validPoints]);

  useEffect(() => {
    const availableUnitTypes = new Set(legendItems.map((item) => item.unitType));
    setHiddenUnitTypes((current) => {
      const next = new Set(Array.from(current).filter((unitType) => availableUnitTypes.has(unitType)));
      return next.size === current.size ? current : next;
    });
  }, [legendItems]);

  const visiblePoints = useMemo(
    () => validPoints.filter((point) => !hiddenUnitTypes.has(normalizeUnitType(point.unitType))),
    [hiddenUnitTypes, validPoints]
  );

  useEffect(() => {
    if (active && hiddenUnitTypes.has(normalizeUnitType(active.unitType))) setActive(null);
  }, [active, hiddenUnitTypes]);

  const toggleUnitType = (unitType) => {
    setHiddenUnitTypes((current) => {
      const next = new Set(current);
      if (next.has(unitType)) next.delete(unitType);
      else next.add(unitType);
      return next;
    });
  };

  const showAllUnitTypes = () => setHiddenUnitTypes(new Set());
  const hideAllUnitTypes = () => setHiddenUnitTypes(new Set(legendItems.map((item) => item.unitType)));

  const heatmapStats = useMemo(() => {
    const weightedPoints = visiblePoints
      .map((point) => ({
        point,
        reach: Number(point.mobility?.totalUsers) || 0,
      }))
      .filter(({ point, reach }) => Number.isFinite(point.lat) && Number.isFinite(point.lng) && reach > 0);
    const maxReach = weightedPoints.reduce((max, item) => Math.max(max, item.reach), 0);
    return { weightedPoints, maxReach };
  }, [visiblePoints]);

  const heatmapCircles = useMemo(() => heatmapStats.weightedPoints.map(({ point, reach }, index) => {
    const intensity = heatmapStats.maxReach ? reach / heatmapStats.maxReach : 0;
    const clamped = Math.max(0.12, Math.min(intensity, 1));
    return {
      id: `${point.id || "heat"}-${index}`,
      lat: point.lat,
      lng: point.lng,
      radius: 180 + clamped * 720,
      fillOpacity: 0.16 + clamped * 0.28,
      color: clamped > 0.66 ? "#dc2626" : clamped > 0.33 ? "#f97316" : "#facc15",
    };
  }), [heatmapStats]);

  const markerIcons = useMemo(() => {
    if (!isLoaded || !window?.google?.maps) return new Map();
    return new Map(legendItems.map((item) => [item.unitType, {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: item.color,
      fillOpacity: 1,
      strokeColor: "#ffffff",
      strokeOpacity: 1,
      strokeWeight: 2,
      scale: 8,
    }]));
  }, [isLoaded, legendItems]);

  const mapMarkers = useMemo(() => {
    const cellSize = clusterCell;
    if (!cellSize) return visiblePoints.map((point) => ({ kind: "point", id: point.id, point }));

    const grouped = new Map();
    visiblePoints.forEach((point) => {
      const key = `${Math.floor(point.lat / cellSize)}:${Math.floor(point.lng / cellSize)}`;
      const current = grouped.get(key) || { count: 0, latSum: 0, lngSum: 0, points: [] };
      current.count += 1;
      current.latSum += point.lat;
      current.lngSum += point.lng;
      current.points.push(point);
      grouped.set(key, current);
    });

    return Array.from(grouped, ([key, group]) => {
      if (group.count === 1) return { kind: "point", id: group.points[0].id, point: group.points[0] };
      return {
        kind: "cluster",
        id: `cluster-${key}`,
        count: group.count,
        lat: group.latSum / group.count,
        lng: group.lngSum / group.count,
      };
    });
  }, [clusterCell, visiblePoints]);

  useEffect(() => {
    if (!active) return;
    const stillVisible = mapMarkers.some((marker) => marker.kind === "point" && marker.id === active.id);
    if (!stillVisible) setActive(null);
  }, [active, mapMarkers]);

  const syncClusterCell = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const nextZoom = map.getZoom();
    if (!Number.isFinite(nextZoom)) return;
    setClusterCell(clusterCellSizeForZoom(nextZoom));
    setHeatScale(heatScaleForZoom(nextZoom));
  }, []);

  const openCluster = useCallback((cluster) => {
    const map = mapRef.current;
    if (!map) return;
    map.panTo({ lat: cluster.lat, lng: cluster.lng });
    map.setZoom(Math.min((map.getZoom() || zoom) + 2, 18));
  }, [zoom]);

  const selectPoint = useCallback((point) => setActive(point), []);

  const exportPng = async () => {
    if (!mapExportRef.current || exporting) return;
    setExporting(true);
    try {
      // Google'ın zoom/Street View/tam ekran butonları çapraz kaynaklı sprite
      // görselleri olduğundan html2canvas bunları doğru çizemiyor; export
      // sırasında geçici olarak gizleyip haritanın bunları kaldırmasını bekliyoruz.
      await new Promise((resolve) => window.setTimeout(resolve, 150));
      const canvas = await html2canvas(mapExportRef.current, {
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        scale: Math.min(window.devicePixelRatio || 1, 2),
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("PNG oluşturulamadı.");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `kampanya-haritasi-${new Date().toISOString().slice(0, 10)}.png`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      alert(`Harita dışa aktarılamadı: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  if (!isLoaded) return <div className="text-slate-500 text-sm py-4">Harita yükleniyor…</div>;

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-end gap-3 border-b border-slate-100 px-7 py-4">
        {/* <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-base font-extrabold text-slate-950">{title}</h3>
          {subtitle && (
            <span className="inline-flex items-center gap-2 rounded-md bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500">
              <span className="h-2 w-2 rounded-full bg-rose-600" />
              {subtitle}
            </span>
          )}
        </div> */}
        <div className="flex items-center gap-2">
          {/* <button
            type="button"
            onClick={() => setShowHeatmap((value) => !value)}
            disabled={!heatmapCircles.length}
            className={`h-9 rounded-lg border px-4 text-xs font-bold disabled:opacity-50 ${
              showHeatmap
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            {showHeatmap ? "Heatmap Kapat" : "Heatmap"}
          </button> */}
          <button
            type="button"
            onClick={exportPng}
            disabled={exporting}
            className="h-9 rounded-lg border border-slate-300 bg-white px-4 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {exporting ? "PNG hazırlanıyor..." : "PNG İndir"}
          </button>
        </div>
      </div>

      <div ref={mapExportRef} className="relative" style={{ height, width: "100%" }}>
        {legendItems.length > 0 && (
          <div
            className="absolute left-3 top-3 z-10 max-h-[70%] min-w-[220px] overflow-auto rounded-lg bg-white p-3"
            // border-radius + box-shadow kombinasyonu html2canvas'ta gölgeyi kutunun içine gri bir
            // ton olarak sızdırıyor (bkz. map-info-card için aynı düzeltme); bu yüzden sadece
            // kenarlık kullanıyoruz, gölge yok.
            style={{ border: "1px solid #e2e8f0" }}
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-xs font-semibold text-slate-700">Ünite Tipleri</div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={showAllUnitTypes} className="rounded border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-600 hover:bg-slate-50">Tümü</button>
                <button type="button" onClick={hideAllUnitTypes} className="rounded border border-slate-200 px-2 py-1 text-[10px] font-semibold text-slate-600 hover:bg-slate-50">Gizle</button>
              </div>
            </div>
            <div className="space-y-1.5">
              {legendItems.map((item) => {
                const isHidden = hiddenUnitTypes.has(item.unitType);
                return (
                  <button
                    key={item.unitType}
                    type="button"
                    onClick={() => toggleUnitType(item.unitType)}
                    className={`flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-xs transition ${
                      isHidden ? "text-slate-400 opacity-55 hover:bg-slate-50" : "text-slate-700 hover:bg-slate-50"
                    }`}
                    title={isHidden ? "Haritada göster" : "Haritada gizle"}
                  >
                    <span
                      className="h-3 w-3 shrink-0 rounded-full border-2 border-white shadow"
                      style={{ backgroundColor: isHidden ? "#cbd5e1" : item.color }}
                    />
                    <span className="flex-1">{item.unitType}</span>
                    <span className="font-medium text-slate-500">{item.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <GoogleMap
          center={center}
          zoom={zoom}
          mapContainerStyle={{ height: "100%", width: "100%" }}
          options={{
            zoomControl: !exporting,
            streetViewControl: !exporting,
            fullscreenControl: !exporting,
            mapTypeControl: !exporting,
            keyboardShortcuts: !exporting,
          }}
          onLoad={(map) => {
            mapRef.current = map;
            window.setTimeout(syncClusterCell, 0);
          }}
          onIdle={syncClusterCell}
          onUnmount={() => {
            mapRef.current = null;
          }}
        >
          {showHeatmap && <HeatmapLayer circles={heatmapCircles} scale={heatScale} />}
          <MarkersLayer
            markers={mapMarkers}
            markerIcons={markerIcons}
            onSelectPoint={selectPoint}
            onOpenCluster={openCluster}
          />
          {active && (
            // Google'ın InfoWindow'u kendi iç DOM'unu (gölge/kuyruk katmanları) enjekte ediyor;
            // bu katmanlar html2canvas'ta düz gri bir kutu olarak çiziliyordu. OverlayViewF ise
            // sadece konumlandırılmış düz bir div döndürdüğü için kartı biz stilleyip güvenle
            // dışa aktarabiliyoruz.
            <OverlayViewF
              position={{ lat: active.lat, lng: active.lng }}
              mapPaneName={FLOAT_PANE}
              getPixelPositionOffset={(width, height) => ({ x: -width / 2, y: -height - 14 })}
            >
              <div className="map-info-bubble">
                <MapInfoCard point={active} onClose={() => setActive(null)} />
                <div className="map-info-arrow" />
              </div>
            </OverlayViewF>
          )}
        </GoogleMap>
      </div>
    </section>
  );
}

const SES_COLORS = {
  A: "#173b63",
  B: "#3f6f9f",
  C: "#79a9d1",
  "D-E": "#cfe1f1",
};

function MapInfoCard({ point, onClose }) {
  const aggregate = point.mobility;
  const metrics = aggregate ? getSesMetrics(aggregate) : [];
  const unitType = normalizeUnitType(point.unitType);
  const renderLegacyInfo = () => (
    <div style={{ marginTop: 10, borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
        Haftalık Erişim: {formatInteger(aggregate.totalUsers)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(90px, 1fr))", gap: 5 }}>
        {metrics.map((metric) => (
          <div key={metric.key} style={{ border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 7px", fontSize: 11 }}>
            <b>SES {metric.label}</b>
            <div>Share: {(metric.share * 100).toFixed(1)}%</div>
            <div>Haftalık Erişim: {formatInteger(metric.reach)}</div>
          </div>
        ))}
      </div>
    </div>
  );

  void renderLegacyInfo;

  return (
    <div className="map-info-card">
      <button type="button" className="map-info-close" aria-label="Kapat" onClick={onClose}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      <div className="map-info-title-row">
        <svg className="map-info-pin" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 22s7-6.18 7-13A7 7 0 0 0 5 9c0 6.82 7 13 7 13Z" fill="currentColor" />
          <circle cx="12" cy="9" r="2.25" fill="#ffffff" />
        </svg>
        <h3>{point.title || "Konum"}</h3>
      </div>

      {unitType && (
        <div className="map-info-unit-badge">
          <span style={{ backgroundColor: markerColor(unitType) }} />
          {unitType}
        </div>
      )}

      {point.img && <img src={point.img} alt={point.title || "Konum"} className="map-info-image" loading="lazy" />}

      {aggregate ? (
        <>
          <div className="map-info-summary">
            <div>
              <div className="map-info-kicker">HAFTALIK ERİŞİM</div>
              <div className="map-info-reach">{formatInteger(aggregate.totalUsers)}</div>
            </div>
            <SesDonut metrics={metrics} />
          </div>

          <div className="map-info-kicker map-info-ses-title">SES DAĞILIMI</div>
          <div className="map-info-table">
            <div />
            <div className="map-info-table-head">PAY</div>
            <div className="map-info-table-head">ERİŞİM</div>
            {metrics.map((metric) => (
              <React.Fragment key={metric.key}>
                <div className="map-info-ses-label">
                  <span style={{ backgroundColor: SES_COLORS[metric.label] || "#94a3b8" }} />
                  SES {metric.label}
                </div>
                <div className="map-info-ses-share">{(metric.share * 100).toFixed(1)}%</div>
                <div className="map-info-ses-reach">{formatInteger(metric.reach)}</div>
              </React.Fragment>
            ))}
          </div>
        </>
      ) : (
        <div className="map-info-empty"></div>
      )}
    </div>
  );
}

function SesDonut({ metrics }) {
  const totalShare = metrics.reduce((total, metric) => total + Math.max(Number(metric.share) || 0, 0), 0);
  let offset = 0;

  if (!totalShare) {
    return (
      <svg className="map-info-donut" viewBox="0 0 120 120" aria-label="SES dağılımı">
        <circle cx="60" cy="60" r="42" fill="none" stroke="#e2e8f0" strokeWidth="28" />
      </svg>
    );
  }

  return (
    <svg className="map-info-donut" viewBox="0 0 120 120" aria-label="SES dağılımı">
      {metrics.map((metric) => {
        const value = Math.max(Number(metric.share) || 0, 0);
        const dashArray = `${value} ${Math.max(totalShare - value, 0)}`;
        const dashOffset = -offset;
        offset += value;
        return (
          <circle
            key={metric.key}
            cx="60"
            cy="60"
            r="42"
            fill="none"
            stroke={SES_COLORS[metric.label] || "#94a3b8"}
            strokeWidth="28"
            strokeDasharray={dashArray}
            strokeDashoffset={dashOffset}
            pathLength={totalShare}
            transform="rotate(-90 60 60)"
          />
        );
      })}
      <circle cx="60" cy="60" r="22" fill="#ffffff" />
    </svg>
  );
}
